<template>
	<div v-if="getFinancialStatementData" class="report_section_container">
		<div class="detail_tab_container">
			<ConnectedSquareTab :items="detailTabItems" :selected-item="selectedTab" @update:detail-tab="onChangePeriod" />
		</div>
		<section class="section_finance">
			<h2 class="report_title">재무제표</h2>
			<div v-if="currentDataStatement && Object.values(currentDataStatement)[0]" class="table_wrap">
				<div class="table_container">
					<table>
						<thead>
							<tr>
								<th></th>
								<th v-for="(item, idx) in Object.keys(currentDataStatement)" :key="idx">
									{{ item }}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in Object.keys(Object.values(currentDataStatement)[0])" :key="item">
								<td class="property">{{ replaceUnderscoreWithSpace(item) }}</td>
								<td v-for="(data, year) in currentDataStatement" :key="year">
									{{ numberFormatWithDecimal(data[item as keyof typeof data]) }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="guide_text">{{ FINANCE_STATEMENT_KR_MIL }}</p>
			</div>
			<div v-else class="emptyData_alert">데이터 없음</div>
		</section>
		<section class="section_finance">
			<h2 class="report_title">재무비율</h2>
			<div v-if="currentDataRatio && Object.values(currentDataRatio)[0]" class="table_wrap">
				<div class="table_container">
					<table>
						<thead>
							<tr>
								<th></th>
								<th v-for="(item, idx) in Object.keys(currentDataRatio)" :key="idx">
									{{ item }}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in Object.keys(Object.values(currentDataRatio)[0])" :key="item">
								<td class="property">{{ replaceUnderscoreWithSpace(item) }}</td>
								<td v-for="(data, year) in currentDataRatio" :key="year">
									{{ numberFormatWithDecimal(data[item as keyof typeof data]) }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="guide_text">{{ FINANCE_RATIO_KR }}</p>
			</div>
			<div v-else :class="'display-none'" class="emptyData_alert">데이터 없음</div>
		</section>
	</div>
</template>

<script setup lang="ts">
// core
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

// api
import { useGetFinancialStatementApi_KR } from '@/api/report';
// component
import ConnectedSquareTab from '@/components/atom/tabBar/ConnectedSquareTab.vue';
// constant
import { FINANCE_RATIO_KR, FINANCE_STATEMENT_KR_MIL } from '@/constants/title';
// util
import { replaceUnderscoreWithSpace } from '@/utils/function/format';
import { numberFormatWithDecimal } from '@/utils/function/format';

// -----variable-----
const route = useRoute();
const detailTabItems = ['연간', '분기'];
const selectedTab = ref();
const currentDataStatement = computed(() => {
	if (!selectedTab.value || selectedTab.value === '연간') {
		return getFinancialStatementData.value?.financial_statement.연간;
	} else {
		return getFinancialStatementData.value?.financial_statement.분기;
	}
});
const currentDataRatio = computed(() => {
	if (!selectedTab.value || selectedTab.value === '연간') {
		return getFinancialStatementData.value?.financial_ratio.연간;
	} else {
		return getFinancialStatementData.value?.financial_ratio.분기;
	}
});

// -----api-----
const { data: getFinancialStatementData } = useGetFinancialStatementApi_KR(
	computed(() => {
		return {
			event_pk: String(route.params.eventPk),
		};
	})
);

// -----function-----
// 연간/분기 변경 시 실행 함수
const onChangePeriod = (tab: string) => {
	selectedTab.value = tab;
};
</script>

<style scoped lang="scss">
.detail_tab_container {
	display: flex;
	justify-content: space-between;
	align-items: center;
}
.report_section_container {
	display: flex;
	flex-direction: column;
	gap: 4.8rem;

	.section_finance {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		.report_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		.report_unit {
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 15.51px;
			color: var(--color-base-light);
		}
		.table_wrap {
			display: flex;
			flex-direction: column;
			gap: 1rem;
		}

		.table_container {
			display: flex;
			flex-direction: column;
			gap: 1rem;
			overflow-x: auto;
			padding-bottom: 1rem;
			table {
				border-collapse: collapse;
				width: 100%;
				th,
				td {
					text-align: center;
					padding: 1rem;
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: 1.551rem;
					border: 1px solid var(--color-base-mid-dark);
				}
				.property {
					background: var(--color-bg-mid, #212c3f);
					width: 1%;
				}
				th {
					text-align: center;
					font-weight: var(--font-w-semi-bold);
					background: var(--color-bg-mid, #212c3f);
				}
				td:first-child {
					text-align: left;
					white-space: nowrap;
					font-weight: var(--font-w-semi-bold);
				}
			}
		}
	}
}
.guide_text {
	font-size: var(--font-s-mid);
	color: var(--color-base-light);
	font-weight: var(--font-w-light);
	line-height: 1.2;
	text-align: right;
	@media screen and (max-width: 768px) {
		text-align: left;
	}
}
.emptyData_alert {
	padding: 3.2rem 0rem;
	font-size: var(--font-s-regular);
	font-weight: var(--font-w-light);
	line-height: 1.2;
	text-align: center;
}
::-webkit-scrollbar {
	width: 17.6rem;
	height: 0.4rem;
}
::-webkit-scrollbar-track {
	background: var(--color-bg);
}
::-webkit-scrollbar-thumb {
	background: var(--color-base-mid);
}
::-webkit-scrollbar-thumb:hover {
	background: #bbb;
}
</style>
